/**
 * TypeScript types for the enhanced sessions view.
 */

import { APISession } from "../../../redux/reducers/scheduleReducer";

/**
 * Time range preset options for filtering sessions.
 */
export type TimeRangePreset =
  | "last_hour"
  | "last_day"
  | "last_week"
  | "last_month"
  | "custom";

/**
 * Filter options for session queries.
 */
export interface SessionFilters {
  // Time filtering
  timeRangePreset?: TimeRangePreset;
  timeFrom?: string; // ISO date string
  timeTo?: string; // ISO date string

  // Organization filtering
  organizationId?: string;

  // Experience filtering (by resource_key)
  assessmentKey?: string;
  roleplayKey?: string;

  // User filtering
  internalUserId?: string;
  userEmailSearch?: string;

  // Session/conversation search
  sessionIdSearch?: string;
  conversationIdSearch?: string;

  // Status filtering
  includeTestInfra?: boolean;
  status?: string[];

  // Codec filtering
  codec?: string;
}

/**
 * Experience and artifact information for a session.
 */
export interface SessionExperienceInfo {
  /** The type of experience: 'assessment' or 'roleplay'. */
  experience_type?: "assessment" | "roleplay";
  /** The resource key for the experience. */
  experience_key?: string;
  /** Human-readable name for the experience. */
  experience_name?: string;
  /** The resource ID for deep-linking to the experience details. */
  experience_id?: string;

  /** Whether an AssessedSession artifact exists for this session. */
  has_artifact: boolean;
  /** The ID of the AssessedSession artifact, if one exists. */
  artifact_id?: string;
  /** The processing state: 'pending', 'complete', or 'failed'. */
  artifact_state?: string;
}

/**
 * Enriched session with experience and artifact information.
 * Uses composition: api_session contains core session data,
 * experience_info contains resolved experience/artifact data.
 */
export interface EnrichedSession {
  /** The core session data. */
  api_session: APISession;
  /** Resolved experience and artifact information, if available. */
  experience_info?: SessionExperienceInfo;
  /** Chat session ID for deep-linking into conversation view. */
  chat_session_id?: string;
  /** The launch_args_id from the session, for reference. */
  launch_args_id?: string;
}

/**
 * Organization summary for filter dropdown.
 */
export interface OrganizationOption {
  id: string;
  name: string;
}

/**
 * Experience summary for filter dropdowns.
 */
export interface ExperienceOption {
  id: string;
  resourceKey: string;
  name: string;
  organizationId: string;
  experienceType: "assessment" | "roleplay";
}

/**
 * Props for the SessionFilters component.
 */
export interface SessionFiltersProps {
  filters: SessionFilters;
  onFiltersChange: (filters: SessionFilters) => void;
  organizations: OrganizationOption[];
  assessments: ExperienceOption[];
  roleplays: ExperienceOption[];
  loading?: boolean;
  /** When true, assessment/roleplay dropdowns show loading state but other filters remain usable. */
  loadingExperiences?: boolean;
}

/**
 * API request for V2 sessions list.
 */
export interface ListSessionsRequestV2 {
  filters?: SessionFilters;
  limit?: number;
  page_number?: number;
  cursor?: string;
}

/**
 * API response for V2 sessions list.
 */
export interface PaginatedSessionsResponseV2 {
  items: EnrichedSession[];
  page_number: number;
  next_cursor?: string;
  has_more_entries: boolean;
  total_count?: number;
}

/**
 * Convert frontend filters to API format.
 */
export function filtersToApiFormat(filters: SessionFilters): Record<string, unknown> {
  return {
    time_range_preset: filters.timeRangePreset,
    time_from: filters.timeFrom,
    time_to: filters.timeTo,
    organization_id: filters.organizationId,
    assessment_key: filters.assessmentKey,
    roleplay_key: filters.roleplayKey,
    internal_user_id: filters.internalUserId,
    user_email_search: filters.userEmailSearch,
    session_id_search: filters.sessionIdSearch,
    conversation_id_search: filters.conversationIdSearch,
    include_test_infra: filters.includeTestInfra ?? true,
    status: filters.status,
    codec: filters.codec,
  };
}

/**
 * Parse URL search params into SessionFilters.
 */
export function parseFiltersFromUrl(searchParams: URLSearchParams): SessionFilters {
  const filters: SessionFilters = {};

  const timeRangePreset = searchParams.get("timeRange");
  if (timeRangePreset) {
    filters.timeRangePreset = timeRangePreset as TimeRangePreset;
  }

  const timeFrom = searchParams.get("timeFrom");
  if (timeFrom) {
    filters.timeFrom = timeFrom;
  }

  const timeTo = searchParams.get("timeTo");
  if (timeTo) {
    filters.timeTo = timeTo;
  }

  const organizationId = searchParams.get("org");
  if (organizationId) {
    filters.organizationId = organizationId;
  }

  const assessmentKey = searchParams.get("assessment");
  if (assessmentKey) {
    filters.assessmentKey = assessmentKey;
  }

  const roleplayKey = searchParams.get("roleplay");
  if (roleplayKey) {
    filters.roleplayKey = roleplayKey;
  }

  const userSearch = searchParams.get("user");
  if (userSearch) {
    filters.userEmailSearch = userSearch;
  }

  const sessionSearch = searchParams.get("session");
  if (sessionSearch) {
    filters.sessionIdSearch = sessionSearch;
  }

  const convSearch = searchParams.get("conversation");
  if (convSearch) {
    filters.conversationIdSearch = convSearch;
  }

  const includeTestInfra = searchParams.get("testInfra");
  if (includeTestInfra !== null) {
    filters.includeTestInfra = includeTestInfra === "true";
  }

  const status = searchParams.get("status");
  if (status) {
    filters.status = status.split(",");
  }

  const codec = searchParams.get("codec");
  if (codec) {
    filters.codec = codec;
  }

  return filters;
}

/**
 * Serialize SessionFilters to URL search params.
 */
export function filtersToUrlParams(filters: SessionFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.timeRangePreset) {
    params.set("timeRange", filters.timeRangePreset);
  }
  if (filters.timeFrom) {
    params.set("timeFrom", filters.timeFrom);
  }
  if (filters.timeTo) {
    params.set("timeTo", filters.timeTo);
  }
  if (filters.organizationId) {
    params.set("org", filters.organizationId);
  }
  if (filters.assessmentKey) {
    params.set("assessment", filters.assessmentKey);
  }
  if (filters.roleplayKey) {
    params.set("roleplay", filters.roleplayKey);
  }
  if (filters.userEmailSearch) {
    params.set("user", filters.userEmailSearch);
  }
  if (filters.sessionIdSearch) {
    params.set("session", filters.sessionIdSearch);
  }
  if (filters.conversationIdSearch) {
    params.set("conversation", filters.conversationIdSearch);
  }
  if (filters.includeTestInfra === false) {
    params.set("testInfra", "false");
  }
  if (filters.status && filters.status.length > 0) {
    params.set("status", filters.status.join(","));
  }
  if (filters.codec) {
    params.set("codec", filters.codec);
  }

  return params;
}
